// My bookings tab grouping (DC62; design My-Bookings.dc.html `inTab`, customer gap
// §A.3 B1) mapped onto the TEN stored statuses (DB-17, DR-46). The design groups its
// short keys; each maps to stored values here, never the other way round:
//   Upcoming      = pending · payment_verification · rejected · confirmed · in_progress
//                   (design: confirmed/pending/payment/running — a rejected PROOF leaves the
//                   booking live and re-uploadable, DB §7.1, so `rejected` stays upcoming)
//   Being checked = payment_verification · rejected (design: payment — incl. its
//                   `proofRejection` variant, which is our stored `rejected`)
//   Past          = completed · no_show
//   Cancelled     = cancelled · refunded · expired (expired is an ended booking the
//                   design has no key for; it lands with the other ended ones)
// `running` is DERIVED (never stored): in_progress, or confirmed with a start today.
import type { Booking } from '../../mock/seeds/schema-types';

export type BookingTab = 'upcoming' | 'verify' | 'past' | 'cancelled';
export const BOOKING_TABS: readonly BookingTab[] = ['upcoming', 'verify', 'past', 'cancelled'];

type Status = Booking['status'];

const TAB_STATUSES: Record<BookingTab, readonly Status[]> = {
  upcoming: ['pending', 'payment_verification', 'rejected', 'confirmed', 'in_progress'],
  verify: ['payment_verification', 'rejected'],
  past: ['completed', 'no_show'],
  cancelled: ['cancelled', 'refunded', 'expired'],
};

export function inTab(tab: BookingTab, status: string): boolean {
  return (TAB_STATUSES[tab] as readonly string[]).includes(status);
}

/** `?tab=` value → tab; the design's legacy `waiting` alias opens Being checked. */
export function parseTab(value: string | null): BookingTab {
  if (value === 'waiting') return 'verify';
  return (BOOKING_TABS as readonly string[]).includes(value ?? '')
    ? (value as BookingTab)
    : 'upcoming';
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** The status to label: the stored one, or the derived `running` display state. */
export function displayStatus(b: Pick<Booking, 'status' | 'starts_at'>, now: Date): string {
  if (b.status === 'in_progress') return 'running';
  if (b.status === 'confirmed' && b.starts_at && sameDay(new Date(b.starts_at), now))
    return 'running';
  return b.status;
}

/** Quiet statuses show the amount instead of a chip on a row (design STATUS.quiet). */
export function isQuiet(display: string): boolean {
  return display === 'confirmed' || display === 'completed';
}

/** Bookings the customer can still act on (reschedule / cancel). */
export function isActionable(status: string): boolean {
  return (
    status === 'pending' ||
    status === 'payment_verification' ||
    status === 'rejected' ||
    status === 'confirmed'
  );
}

// Upcoming lists run soonest-first (design); ended lists run newest-first so a long
// history opens on the latest month.
export function sortForTab(tab: BookingTab, items: Booking[]): Booking[] {
  const key = (b: Booking) => (b.starts_at ? new Date(b.starts_at).getTime() : 0);
  const asc = tab === 'upcoming' || tab === 'verify';
  return [...items].sort((a, b) => (asc ? key(a) - key(b) : key(b) - key(a)));
}

/** Case-insensitive match on service, business (both languages) and booking code. */
export function matchesQuery(b: Booking, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = [
    ...(b.items ?? []).map((i) => i.name_snapshot),
    b.business?.name,
    b.business?.name_bn,
    b.code,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}
